import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { Download } from "lucide-react";
import { type OverallStatistics } from "@/lib/statistics-api.service";
import { downloadChartPng } from "./utils";
import { DonutBreakdown, STATUS_COLORS, type DonutChart } from "./donut-breakdown";
import { StatisticsFilterControls, type StatisticsFilters } from "./StatisticsControls";

interface OverviewTabProps {
	overallStats: OverallStatistics | null;
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
}

const formatCount = (n: number) => n.toLocaleString("vi-VN");

/**
 * Ba trạng thái RỜI NHAU của một hồ sơ, cộng lại đúng bằng tổng số sinh viên.
 *
 * Tiếp nhận và học phí là hai chặng của cùng một phễu — đóng học phí trước rồi
 * mới đến làm thủ tục — nên vẽ hai vòng tròn riêng là kể cùng một câu chuyện
 * hai lần, mà lại không cho biết ai đang kẹt ở giữa.
 *
 * Chặng giữa là HỢP của hai nhóm: đã đóng học phí, HOẶC đã tiếp nhận mà khoản
 * thu chưa về tới hệ thống. Server đếm hợp đó một lần bằng `OR`
 * (`paidOrReceivedStudents`) — cộng `paidStudents` với `receivedButUnpaidStudents`
 * ở đây sẽ đếm hai lần những hồ sơ thuộc cả hai nhóm.
 *
 * Vẫn kẹp bằng `min`/`max`: dữ liệu lệch thì biểu đồ không có lát âm, không vượt
 * tổng, và ba lát vẫn cộng đúng bằng tổng.
 */
export function charts(stats: OverallStatistics): DonutChart[] {
	const paidOrReceived = Math.min(stats.totalStudents, stats.paidOrReceivedStudents);
	const received = Math.min(stats.receivedStudents, paidOrReceived);
	const paidNotReceived = Math.max(0, paidOrReceived - received);
	const notPaid = Math.max(0, stats.totalStudents - received - paidNotReceived);
	const withGender = stats.malePaidOrReceived + stats.femalePaidOrReceived;

	const result: DonutChart[] = [
		{
			title: "Xác nhận nhập học",
			filename: "tong-quan-xac-nhan-nhap-hoc",
			total: {
				label: "Tổng số sinh viên",
				value: stats.totalStudents,
				hint: "tổng số sinh viên được gọi nhập học",
			},
			// Con số ở tâm là hai lát đầu gộp lại: câu hỏi của cả trang là "bao nhiêu
			// sinh viên đã xác nhận nhập học", còn việc họ dừng ở chặng nào mới là
			// chi tiết của từng lát.
			highlight: { label: "Đã xác nhận nhập học", value: paidOrReceived },
			segments: [
				{
					label: "Đã tiếp nhận",
					value: received,
					color: STATUS_COLORS.done,
					hint:
						stats.receivedButUnpaidStudents > 0
							? `trong đó ${formatCount(stats.receivedButUnpaidStudents)} chưa ghi nhận học phí`
							: "đã đóng học phí và làm thủ tục",
				},
				{
					label: "Đã đóng học phí, chưa tiếp nhận",
					value: paidNotReceived,
					color: STATUS_COLORS.pending,
					textColor: STATUS_COLORS.pendingText,
					hint: "chờ đến làm thủ tục",
				},
				{
					label: "Chưa xác nhận nhập học",
					value: notPaid,
					color: STATUS_COLORS.idle,
					textColor: STATUS_COLORS.idleText,
					hint: "chưa đóng học phí, chưa tiếp nhận",
				},
			],
		},
	];

	// Cùng mẫu số với vòng tròn trên — tổng sinh viên — chứ không phải riêng nhóm
	// đã xác nhận. Hai vòng tròn nằm cạnh nhau, cùng đường kính: mẫu số khác nhau
	// thì cùng một lát bánh ở hai hình lại mang hai nghĩa, và người đọc dễ hiểu
	// 3.043 nam là trên tổng. Phần chưa xác nhận vào lát xám, đúng bằng lát xám
	// của vòng tròn trên, nên hai vành màu so sánh trực tiếp được.
	//
	// Đổi lại, tỉ lệ nam/nữ trong nội bộ nhóm đã xác nhận không còn đọc được từ
	// vòng tròn, nên nó xuống dòng phụ của card.
	//
	// `max` là chốt chặn cho dữ liệu lệch: đếm được nhiều giới tính hơn số hồ sơ
	// của chặng thì lấy chính hai lát làm mẫu số, để tỉ lệ không vượt quá 100%.
	if (withGender > 0) {
		const confirmed = Math.max(paidOrReceived, withGender);
		const unconfirmed = Math.max(0, stats.totalStudents - confirmed);
		// Dòng phụ này chỉ có nghĩa khi nhóm đã xác nhận là một phần THỰC SỰ NHỎ
		// HƠN tổng. Với một khoá đã đóng — khoá 2025 trên production: 4804/4804 đã
		// xác nhận — hai mẫu số bằng nhau, và card hiện ra "66.4% · 66.4% trong
		// nhóm đã xác nhận": cùng một con số nói hai lần.
		const shareOfConfirmed = (value: number) =>
			confirmed > 0 && confirmed < stats.totalStudents
				? `${((value / confirmed) * 100).toFixed(1)}% trong nhóm đã xác nhận`
				: "";

		result.push({
			title: "Giới tính",
			filename: "tong-quan-gioi-tinh",
			total: {
				label: "Tổng số sinh viên",
				value: stats.totalStudents,
				// Card này đã có ở vòng tròn trên, cùng con số; nhắc lại chỉ tổ dài.
				showCard: false,
			},
			segments: [
				{
					label: "Nam",
					value: stats.malePaidOrReceived,
					color: STATUS_COLORS.male,
					hint: shareOfConfirmed(stats.malePaidOrReceived),
				},
				{
					label: "Nữ",
					value: stats.femalePaidOrReceived,
					color: STATUS_COLORS.female,
					hint: shareOfConfirmed(stats.femalePaidOrReceived),
				},
				// Lát xám cùng kích thước với lát xám của vòng tròn trên: hai biểu đồ
				// dùng chung mẫu số nên vành màu của chúng so sánh trực tiếp được.
				// Không sinh card — con số này đã đứng ở "Chưa xác nhận nhập học".
				{
					label: "Chưa xác nhận nhập học",
					value: unconfirmed,
					color: STATUS_COLORS.idle,
					showCard: false,
				},
			],
		});
	}

	return result;
}

export default function OverviewTab({ overallStats, filters, onFiltersChange }: OverviewTabProps) {
	const overviewRef = useRef<HTMLDivElement>(null);

	return (
		<TabsContent value="overview">
			{/* The khoa filter has to reach every tab, and this was the one tab with no
			    controls at all; it gains the date range along with them. Rendered
			    outside the screenshot ref so "Tải PNG" captures the numbers, not the
			    form. */}
			{onFiltersChange && (
				<div className="mb-4 md:mb-6">
					<StatisticsFilterControls
						filterType="total"
						onFilterChange={() => {}}
						chartLimit={10}
						onChartLimitChange={() => {}}
						showChartLimit={false}
						showFilterType={false}
						filters={filters}
						onFiltersChange={onFiltersChange}
						showFilters
					/>
				</div>
			)}

			<div ref={overviewRef} className="space-y-4 md:space-y-6">
				{/* Không có tiêu đề riêng: tiêu đề trang "Thống kê" và tab "Tổng quan"
				    đang mở đã nói đúng điều đó rồi. Chỉ còn nút tải cả tab. */}
				<div className="flex items-center justify-end">
					<Button
						onClick={() => downloadChartPng(overviewRef, "thong-ke-tong-quan")}
						variant="outline"
						size="sm"
						className="flex items-center gap-2 w-fit"
					>
						<Download className="h-4 w-4" />
						<span className="hidden sm:inline">Tải PNG</span>
						<span className="sm:hidden">PNG</span>
					</Button>
				</div>

				{overallStats && <DonutBreakdown charts={charts(overallStats)} />}
			</div>
		</TabsContent>
	);
}
